import React, { useState } from 'react';
import { Text, ActionIcon, Tooltip, Badge, Group } from '@mantine/core';
import { IconCopy, IconChevronDown, IconChevronUp } from '@tabler/icons-react';

interface GuidDisplayProps {
    value: string;
    variant?: 'compact' | 'expanded' | 'chip';
    showCopyButton?: boolean;
    showExpandButton?: boolean;
    fontFamily?: string;
    fontSize?: string;
}

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = (value: string): boolean => {
    return GUID_REGEX.test(value);
};

export const formatGuid = (guid: string): string => {
    if (!isGuid(guid)) return guid;
    const cleanGuid = guid.toLowerCase().replace(/[^0-9a-f]/g, '');
    if (cleanGuid.length !== 32) return guid;
    return `${cleanGuid.slice(0, 8)}-${cleanGuid.slice(8, 12)}-${cleanGuid.slice(12, 16)}-${cleanGuid.slice(16, 20)}-${cleanGuid.slice(20, 32)}`;
};

import { copyToClipboard } from '../../utils/clipboardUtils';

export const GuidDisplay: React.FC<GuidDisplayProps> = ({
    value,
    variant = 'compact',
    showCopyButton = true,
    showExpandButton = true,
    fontFamily = 'monospace',
    fontSize = '0.875rem'
}) => {
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);

    if (!isGuid(value)) {
        return <Text ff={fontFamily} fz={fontSize}>{value}</Text>;
    }

    const formattedGuid = formatGuid(value);
    const shortGuid = `${formattedGuid.slice(0, 8)}...`;

    const handleCopy = async () => {
        const success = await copyToClipboard(formattedGuid);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    if (variant === 'chip') {
        return (
            <Group gap={4} align="center" wrap="nowrap">
                <Badge
                    variant="outline"
                    size="sm"
                    ff={fontFamily}
                    fz="xs"
                    style={{
                        cursor: showExpandButton ? 'pointer' : 'default',
                        maxWidth: expanded ? 'none' : '120px',
                        textTransform: 'none'
                    }}
                    onClick={showExpandButton ? handleExpand : undefined}
                >
                    {expanded ? formattedGuid : shortGuid}
                </Badge>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy GUID"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    if (variant === 'expanded') {
        return (
            <Group gap="xs" align="center" wrap="nowrap">
                <Text
                    ff={fontFamily}
                    fz={fontSize}
                    px="xs"
                    py={4}
                    style={{
                        color: 'var(--mantine-color-text)',
                        backgroundColor: 'var(--mantine-color-default-hover)',
                        borderRadius: '4px',
                        border: '1px solid var(--mantine-color-default-border)'
                    }}
                >
                    {formattedGuid}
                </Text>
                {showCopyButton && (
                    <Tooltip label={copied ? "Copied!" : "Copy GUID"}>
                        <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                            <IconCopy size={14} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        );
    }

    return (
        <Group gap="xs" align="center" wrap="nowrap">
            <Text
                ff={fontFamily}
                fz={fontSize}
                px={6}
                py={2}
                style={{
                    color: 'var(--mantine-color-text)',
                    backgroundColor: 'var(--mantine-color-default-hover)',
                    borderRadius: '3px',
                    border: '1px solid var(--mantine-color-default-border)',
                    cursor: 'pointer'
                }}
                onClick={handleCopy}
            >
                {expanded ? formattedGuid : shortGuid}
            </Text>

            {showExpandButton && (
                <Tooltip label={expanded ? "Show less" : "Show full GUID"}>
                    <ActionIcon variant="subtle" size="sm" onClick={handleExpand}>
                        {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                    </ActionIcon>
                </Tooltip>
            )}

            {showCopyButton && (
                <Tooltip label={copied ? "Copied!" : "Copy GUID"}>
                    <ActionIcon variant="subtle" size="sm" onClick={handleCopy} color={copied ? "green" : "gray"}>
                        <IconCopy size={14} />
                    </ActionIcon>
                </Tooltip>
            )}
        </Group>
    );
};
